// bim-viewer.service.ts
// Beispiel-Service für die Integration der BIC-BIM-Viewer in Angular

import {DestroyRef, inject, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BehaviorSubject, Observable} from 'rxjs';
import {
  BcfTopicData,
  BcfTopicInput,
  BimAppConfig,
  BimAppService,
  CategoryTreeNode,
  FileSelectRequest,
  FragmentsModel,
  PropertyGroup,
  SelectedElementRef,
  SpatialTreeNode
} from '@buildinginformationcloud/bic-bim-viewer';
import {ProjectFile, ProjectFileControllerService} from "../../api";
import {StateConfigService} from "../state/state-config.service";
import {Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {
  NewCoordinationModelComponent,
  SelectModelsForBimViewerComponent
} from "../../components/files/components/pop-ups/pop-ups";
import {PermissionService} from "../permission/permission.service";
import {NotificationsService} from "../notifications/notifications.service";
import {coordinationModelErrorMessage, coordinationModelPath} from "./coordination-model-error";
import {
  ChatPropertyRef,
  ChatViewerAction,
  ChatViewerActionResult,
  ChatViewerQuery,
  ChatViewerQueryResult,
  ChatViewerSection,
  ChatViewerSnapshot,
  ChatViewerTarget
} from "./bim-viewer-chat.model";
import {asNumber, ATTRIBUTE_PSET, IfcPropertyRow, matchesAll, propertyRows, rowsOf} from "./ifc-properties";
import {sectionPlanes} from "./viewer-sections";
import {
  hasOpeningRelations,
  hasSpaceBoundaries,
  IfcRelationModel,
  relatedVia,
  typeEntries,
  typeNamesOf
} from "./ifc-relations";

/** Obergrenzen für den Chat-Kontext – er reist mit jeder Nachricht mit. */
const CHAT_MAX_SELECTION = 20;
const CHAT_MAX_PROPERTY_ROWS = 80;

/** Obergrenzen für Abfrage-Ergebnisse – sie gehen ins Kontextfenster des Modells. */
const CHAT_QUERY_MAX_ELEMENTS = 50;
const CHAT_QUERY_MAX_ROWS_PER_ELEMENT = 60;
const CHAT_QUERY_MAX_VALUES = 50;
/** So viele BCF-Themen meldet `bcf_topics` höchstens. */
const CHAT_QUERY_MAX_TOPICS = 100;
const CHAT_QUERY_MAX_NAMES = 200;
const CHAT_QUERY_MAX_CHARS = 30_000;

/**
 * Farben für `color_by`, gut unterscheidbar; die letzte (Grau) steht für „Sonstige", wenn es mehr
 * Werte als Farben gibt.
 */
const CHAT_COLOR_PALETTE = [
  '#E53935', '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1',
  '#FDD835', '#6D4C41', '#D81B60', '#3949AB', '#7CB342', '#9E9E9E',
];

const refKey = (ref: SelectedElementRef) => `${ref.modelId}:${ref.localId}`;

/** Ob ein Ziel nichts eingrenzt – dann gilt bei Abfragen und `color_by` das ganze Modell. */
const isEmptyTarget = (target?: ChatViewerTarget) => !target || (!target.guids?.length && !target.ifcClass
  && !target.storey && !target.ifcType && !target.selection && !target.where?.length);

/** Klassen- und Geschossindex der geladenen Modelle, auf den chatBIC seine Ziele auflöst. */
interface ChatModelIndex {
  /** Sortierte Modell-Ids – ändern sie sich, ist der Index veraltet. */
  key: string;
  /** IFC-Klasse (UPPERCASE) → Elemente. */
  classes: Map<string, SelectedElementRef[]>;
  /** Geschossname → Elemente darin. */
  storeys: Map<string, SelectedElementRef[]>;
  /** Typname → Typklassen und Elemente (IfcRelDefinesByType). */
  types: Map<string, { ifcClasses: Set<string>; items: SelectedElementRef[] }>;
  /** Ob alle Modelle die Öffnungsrelationen tragen (siehe hasOpeningRelations). */
  openingRelations: boolean;
  /** Ob alle Modelle Raumbegrenzungen tragen (siehe hasSpaceBoundaries). */
  spaceBoundaries: boolean;
  /** Alle Elemente aus {@link classes}, ohne Dubletten – das „ganze Modell" für Abfragen. */
  all: SelectedElementRef[];
  /** `modelId:localId` → IFC-Klasse, Typname, Geschossname. */
  classByItem: Map<string, string>;
  typeByItem: Map<string, string>;
  storeyByItem: Map<string, string>;
}


export interface TopicCreationData {
  title: string,
  description: string,
  guids: string[],
  labels?: string[]
  /** Spezifikation/Prüfregel - für die Gruppierung "Pro Spezifikation" im BCF-Panel. */
  specification?: string,
  /** Anforderung innerhalb der Spezifikation - für "Pro Anforderung"; leer beim Basis-Check. */
  requirement?: string,
}


/**
 * Ein im Viewer zu ladendes Modell.
 *
 * `name` ist gleichzeitig die Modell-Id in der Engine (siehe BimApp.loadModel). `fileId` ist die
 * Bruecke zur Persistenz: der Viewer kannte bisher nur Name und URL, weshalb ein vergebener Tag
 * nirgends gespeichert werden konnte.
 */
export interface ViewerModelSource {
  name: string;
  url: string;
  /** ProjectFile-Id des Fachmodells; fehlt bei Modellen aus dem lokalen Datei-Dialog. */
  fileId?: string;
  /** Bereits gespeicherter Tag, wird nach dem Laden angewandt. */
  tag?: string;
}

export interface ViewerState {
  isInitialized: boolean;
  models: Map<string, FragmentsModel>;
  selectedElements: string[];
  measurements: any[];
  clipperEnabled: boolean;
  measurementMode: 'none' | 'length' | 'area';
}

@Injectable({
  providedIn: 'root'
})
export class BimViewerService {
  /**
   * Der Viewer-Service der Library. Er ist selbst `providedIn: 'root'`, wird
   * aber erst durch `<lib-bic-bim-viewer>` initialisiert – vorher wirft jeder
   * Zugriff auf die Engine. Deshalb wird hier über `viewerReady` verfolgt, ob
   * die Initialisierung durch (ready) bereits gemeldet wurde.
   */
  private bimApp = inject(BimAppService);
  private viewerReady = false;
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private stateConfig = inject(StateConfigService);
  private dialog = inject(MatDialog);
  private fileControllerService = inject(ProjectFileControllerService);
  private permissionService = inject(PermissionService);
  private notificationsService = inject(NotificationsService);

  /**
   * Modellname (= Modell-Id in der Engine) → ProjectFile-Id. Erste Registrierung gewinnt.
   *
   * Namensdubletten sind im Viewer ohnehin nicht darstellbar (die Engine keyt Modelle über den
   * Namen); die zweite gleichnamige Datei bekommt deshalb keine Zuordnung und ihr Tag wird nicht
   * gespeichert -- besser, als ihn der falschen Datei zuzuschreiben.
   */
  private readonly modelFileIds = new Map<string, string>();

  /**
   * Letzter zum Server geschriebener bzw. von dort gelesener Tag je Modell. Verhindert, dass das
   * Anwenden eines geladenen Tags als Nutzeränderung zurückgeschrieben wird.
   */
  private readonly lastPersistedTags = new Map<string, string>();

  /** Aktuelle Auswahl als Referenzen – `state.selectedElements` kennt nur Strings. */
  private lastSelection: SelectedElementRef[] = [];

  /** Wird beim ersten Chat-Zugriff aufgebaut; Kategorien- und Strukturbaum sind teuer. */
  private chatIndex?: Promise<ChatModelIndex>;
  private chatIndexKey?: string;

  /** Properties je Element (`modelId:localId`) für chatBICs Filter und Abfragen, je Modell-Set. */
  private chatPropertyCache?: { key: string; rows: Map<string, IfcPropertyRow[]> };

  private stateSubject = new BehaviorSubject<ViewerState>({
    isInitialized: false,
    models: new Map(),
    selectedElements: [],
    measurements: [],
    clipperEnabled: false,
    measurementMode: 'none'
  });

  public state$: Observable<ViewerState> = this.stateSubject.asObservable();

  constructor() {
    // Die Library meldet Ereignisse über Observables (früher: Callback-Handler
    // in initBimApp). Das Abonnement muss vor der Initialisierung stehen –
    // insbesondere fileSelectRequested$, denn daran erkennt der Viewer, dass
    // der Host die Modellauswahl übernimmt (statt einen Datei-Dialog des
    // Betriebssystems zu öffnen).
    this.bimApp.modelLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(model => this.handleModelLoaded(model));
    this.bimApp.elementsSelected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(elements => this.handleElementsSelected(elements));
    this.bimApp.fileSelectRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(request => this.onFileSelectRequested(request));
    // Deckt beide Eingabewege ab: die Abfrage im Ladescreen und das nachträgliche Bearbeiten im
    // Strukturbaum. Vorher lebte der Tag nur im Speicher der Engine und war nach jedem Reload weg.
    this.bimApp.modelTagChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(modelId => this.persistModelTag(modelId));
  }

  /**
   * Loest eine Auswahl aus dem Modell-Dialog in ladbare IFC-Dateien auf.
   *
   * Koordinationsmodelle werden durch ihre verknuepften Fachmodelle ersetzt; bereits geladene und
   * doppelt vorkommende Dateien fallen heraus (dasselbe Fachmodell kann in mehreren
   * Koordinationsmodellen stecken).
   */
  private static resolveToIfcFiles(selection: ProjectFile[], loadedFileIds: Set<string>): ProjectFile[] {
    const resolved: ProjectFile[] = [];
    const seen = new Set<string>(loadedFileIds);

    for (const entry of selection) {
      const candidates = entry.fileType === ProjectFile.FileTypeEnum.COORDINATION
        ? (entry.linkedFiles ?? [])
        : [entry];
      for (const candidate of candidates) {
        if (!candidate.id || !candidate.link || seen.has(candidate.id)) continue;
        seen.add(candidate.id);
        resolved.push(candidate);
      }
    }

    return resolved;
  }

  /**
   * Lädt ein oder mehrere IFC-Modelle/Fragments.
   *
   * @param models Zu ladende Modelle; Dubletten nach Namen werden übersprungen, weil der Name in
   *   der Engine die Modell-Id ist.
   * @param hasTopics Gibt an, ob Topics aus dem localStorage geladen werden sollen
   */
  async loadModelFromUrl(models: ViewerModelSource[], hasTopics: boolean): Promise<void> {
    if (!this.viewerReady) {
      throw new Error('BIM App ist nicht initialisiert');
    }

    // Alle Modelle gemeinsam starten. Die Viewer-Bibliothek bündelt parallel gestartete Ladevorgänge
    // in einem Popup; sequenzielle Aufrufe würden für jedes Modell ein eigenes Popup öffnen.
    await Promise.all(models.map(async model => {
      this.registerModelFile(model.name, model.fileId);
      await this.bimApp.loadModel(model.url, model.name, BimViewerService.fileTypeOf(model.url, model.name));
      if (model.tag) {
        // Reihenfolge ist tragend: erst als "bereits persistiert" merken, dann setzen. Sonst löst
        // das Anwenden des gespeicherten Tags über modelTagChanged$ sofort ein PUT mit demselben
        // Wert aus.
        this.lastPersistedTags.set(model.name, model.tag);
        this.bimApp.setModelTag(model.name, model.tag);
      }
    }));

    if (hasTopics) {
      const topics = localStorage.getItem('topics');
      if (topics) {
        const parsedTopics: TopicCreationData[] = JSON.parse(topics);
        for (const topic of parsedTopics) {
          await this.createTopic({
            title: topic.title,
            description: topic.description,
            labels: topic.labels,
            specification: topic.specification,
            requirement: topic.requirement,
          }, topic.guids);
        }
      }

      // Vorgabe: nach dem Öffnen aus einem Bericht sollen die Issues direkt sichtbar sein, statt
      // erst per Klick auf das BCF-Panel im Viewer.
      this.bimApp.showBCFPanel();
    }
  }

  /** Namen der aktuell geladenen Modelle – für den Titel im eigenen Header. */
  readonly loadedModelNames$ = this.bimApp.loadedModelNames$;

  /**
   * Config für `<lib-bic-bim-viewer>`. `routeLayoutSync` bleibt aus (Default):
   * die Library würde sonst absolut auf `/viewer` bzw. `/viewer-bcf` navigieren
   * und damit die Datei-Route verlassen.
   */
  get viewerConfig(): Omit<BimAppConfig, 'container'> {
    return {
      enablePostproduction: true,
      workerPath: '/assets/worker.mjs',
      bcfAuthor: this.stateConfig.user.mail,
      // planBIC hat seinen eigenen app-page-header und stellt Modellname,
      // Suche und "IFC Öffnen" selbst – ohne das stehen zwei Leisten
      // übereinander.
      toolbar: {visible: false},
    };
  }

  /**
   * Öffnet die Modellauswahl für den Button im eigenen Header. Läuft über
   * `requestFileSelection`, damit derselbe Pfad wie beim viewer-eigenen Button
   * greift und {@link onFileSelectRequested} den Projektdatei-Dialog zeigt.
   */
  openModelSelection(): void {
    // requestFileSelection geht über getApp() und wirft vor der Initialisierung.
    if (!this.viewerReady) return;

    this.bimApp.requestFileSelection('ifc');
  }

  /** true, sobald die Engine steht (siehe {@link onViewerReady}). */
  get isViewerReady(): boolean {
    return this.viewerReady;
  }

  get bcfViewActivated(): boolean {
    return this.viewerReady && this.bimApp.isBCFPanelVisible();
  }

  /**
   * Ermittelt den Modelltyp aus der URL – Fragments-Links enden auf `.frag`,
   * während der Anzeigename weiterhin der IFC-Dateiname ist – und fällt auf
   * den Namen zurück.
   */
  private static fileTypeOf(url: string, name: string): 'ifc' | 'frag' {
    const path = url.split('?')[0].split('#')[0].toLowerCase();
    if (path.endsWith('.frag')) return 'frag';
    if (path.endsWith('.ifc')) return 'ifc';

    return name.toLowerCase().endsWith('.frag') ? 'frag' : 'ifc';
  }

  /**
   * Wird aus dem (ready)-Output von `<lib-bic-bim-viewer>` aufgerufen, sobald
   * die Engine steht. Erst danach darf auf sie zugegriffen werden.
   */
  onViewerReady(): void {
    this.viewerReady = true;
    this.updateState({isInitialized: true});
  }

  /**
   * Entfernt ein Modell
   */
  removeModel(modelId: string): void {
    if (!this.viewerReady) return;

    this.bimApp.removeModel(modelId);
    this.modelFileIds.delete(modelId);
    this.lastPersistedTags.delete(modelId);
    const currentState = this.stateSubject.value;
    const newModels = new Map(currentState.models);
    newModels.delete(modelId);

    this.updateState({models: newModels});
  }

  /**
   * Wird vom BIM-Viewer aufgerufen, wenn eine Datei ausgewählt werden soll.
   * Öffnet einen Dialog zur Auswahl von IFC-Modellen aus dem Projekt.
   */
  onFileSelectRequested(request: FileSelectRequest): void {
    const projectId = this.stateConfig.getProjectId();

    // Bereits geladene Datei-IDs aus der URL ermitteln
    const {coordinationIds, allIds: loadedFileIds} = this.getLoadedFileIds();

    // Dialog sofort öffnen und Daten asynchron nachladen, damit direkt
    // visuelles Feedback vorhanden ist.
    const dialogRef = this.dialog.open(SelectModelsForBimViewerComponent, {
      width: '560px',
      data: {
        ifcFiles: [],
        allowMultiple: true,
        loading: true
      }
    });

    // Verarbeite das Ergebnis
    dialogRef.afterClosed().subscribe(async result => {
      if (result && result.selectedFiles && result.selectedFiles.length > 0) {
        // Ein ausgewaehltes Koordinationsmodell steht fuer seine verknuepften Fachmodelle: die
        // Engine kann nur IFC/Fragments laden, und die Route erwartet ebenfalls IFC-Ids.
        const selectedFiles: ProjectFile[] = BimViewerService.resolveToIfcFiles(
          result.selectedFiles,
          loadedFileIds,
        );
        if (selectedFiles.length === 0) return;

        // Erstes Modell als File mit korrektem Namen laden,
        // damit der Viewer den Dateinamen statt der URL verwendet
        const response = await fetch(selectedFiles[0].link!);
        const blob = await response.blob();
        const file = new File([blob], selectedFiles[0].name!, {type: 'application/octet-stream'});
        // Dieses Modell laeuft nicht ueber loadModelFromUrl, die Zuordnung fuer die Tag-Persistenz
        // muss hier von Hand passieren.
        this.registerModelFile(selectedFiles[0].name!, selectedFiles[0].id);
        if (selectedFiles[0].modelTag) {
          this.lastPersistedTags.set(selectedFiles[0].name!, selectedFiles[0].modelTag);
        }
        request.onFileSelected(file);

        // Weitere Modelle direkt laden
        if (selectedFiles.length > 1) {
          this.loadModelFromUrl(
            selectedFiles.slice(1).map(f => ({
              name: f.name!,
              url: f.link!,
              fileId: f.id,
              tag: f.modelTag ?? undefined,
            })),
            false,
          ).catch(err => {
            console.error(`Fehler beim Laden der Dateien:`, err);
          });
        }

        // URL aktualisieren: Neue Datei-IDs zum coordination-Parameter hinzufügen
        const newCoordinationIds = [
          ...coordinationIds,
          ...selectedFiles.map(f => f.id!)
        ];
        const currentUrlTree = this.router.parseUrl(this.router.url);
        currentUrlTree.queryParams = {
          ...currentUrlTree.queryParams,
          coordination: newCoordinationIds.join(',')
        };
        this.router.navigateByUrl(currentUrlTree, {replaceUrl: true});
      } else {
        // Abbrechen
        request.onCancel?.();
      }
    });

    // Lade alle Dateien aus dem Projekt
    this.fileControllerService.getFiles(projectId).subscribe({
      next: (files) => {
        // Bereits geöffnete Modelle anhand ihrer ID herausfiltern, nur IFC-Dateien anzeigen
        const ifcFiles = files.filter(f =>
          !f.isFolder && f.link &&
          f.fileType === ProjectFile.FileTypeEnum.IFC &&
          !loadedFileIds.has(f.id!)
        );

        // Gespeicherte Koordinationsmodelle mit anbieten. Vorher waren sie im Viewer unerreichbar --
        // der Filter oben liess nur IFC durch, sodass der Weg ueber den Dateimanager und einen neuen
        // Tab fuehrte. GET /files liefert linkedFiles mit, es braucht also kein Nachladen.
        const coordinationModels = files.filter(f =>
          !f.isFolder &&
          f.fileType === ProjectFile.FileTypeEnum.COORDINATION &&
          (f.linkedFiles ?? []).some(linked => linked.link && !loadedFileIds.has(linked.id!))
        );
        dialogRef.componentInstance?.setIfcFiles(ifcFiles, coordinationModels);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Dateien:', error);
        dialogRef.componentInstance?.setLoadingFailed();
      }
    });
  }

  /**
   * Gibt den aktuellen Zustand der BIC-BIM-Viewer zurück
   */
  getCurrentState(): ViewerState {
    return this.stateSubject.value;
  }

  /**
   * Gibt alle geladenen Modelle zurück
   */
  getModels(): Map<string, FragmentsModel> {
    return this.viewerReady ? this.bimApp.getModels() : new Map();
  }

  async createTopic(topicData: BcfTopicInput, guids: string[]): Promise<BcfTopicData> {
    const topic = this.bimApp.createTopic(topicData);

    if (guids && guids.length > 0) {
      // instant: true – diese Methode wird ausschließlich für den Bulk-Import aus
      // loadModelFromUrl() aufgerufen (ein Aufruf pro BCF-Issue, sequenziell). Ohne
      // "instant" fährt die Kamera bei jedem Issue eine animierte Transition ab
      // (siehe createViewpointFromElements), was bei vielen Issues den Großteil der
      // gesamten Ladezeit ausmacht, obwohl hier noch niemand zusieht.
      await this.bimApp.createViewpointFromElements(guids, topic.guid, {instant: true});
    }

    return topic;
  }

  /**
   * Räumt die BIC-BIM-Viewer auf.
   *
   * Muss beim Verlassen des Viewers passieren: `BimAppService` ist
   * `providedIn: 'root'` und überlebt `<lib-bic-bim-viewer>`, sodass ein
   * erneutes Öffnen sonst einen zweiten Render-Loop starten würde.
   */
  dispose(): void {
    if (!this.viewerReady) return;

    this.bimApp.dispose();
    this.viewerReady = false;
    this.lastSelection = [];
    this.chatIndex = undefined;
    this.chatIndexKey = undefined;
    this.chatPropertyCache = undefined;

    this.updateState({
      isInitialized: false,
      models: new Map(),
      selectedElements: [],
    });
  }

  // Private Handler-Methoden

  private handleModelLoaded(model: FragmentsModel): void {
    console.log('Modell geladen:', model);

    const currentState = this.stateSubject.value;
    const newModels = new Map(currentState.models);
    newModels.set(model.modelId, model);

    this.updateState({models: newModels});
  }

  toggleBcfView(): void {
    if (!this.viewerReady) return;

    this.bimApp.toggleBCFPanel();
  }

  private updateState(partial: Partial<ViewerState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({...currentState, ...partial});
  }

  /**
   * Öffnet eine Projektdatei im Viewer und legt die übergebenen BCF-Topics an (siehe
   * `loadModelFromUrl`, das sie nach dem Laden aus dem localStorage erzeugt).
   *
   * `additionalFiles` sind weitere IFC-Dateien, die gemeinsam mit `projectFile` geladen werden
   * sollen (z. B. alle Dateien eines Multi-File-IDS-Berichts) – sie laufen über denselben
   * `coordination`-Query-Parameter wie `IfcViewerComponent.openCoordinationModel`, den
   * `FileViewerComponent` beim Laden der Route ausliest. Es wird KEIN persistentes
   * Koordinationsmodell angelegt (siehe {@link createCoordinationModel}) – nur eine gemeinsame
   * Sitzung im Viewer.
   */
  open(projectFile: ProjectFile, topics: TopicCreationData[], additionalFiles: ProjectFile[] = [],
       report?: { reportId?: string; reportType?: string }) {
    localStorage.setItem('topics', JSON.stringify(topics));
    const queryParams: Record<string, string> = {topics: 'true'};
    // Welcher Bericht hier gezeigt wird. Liest chatBIC aus der Route (`ChatRouteContext`), um im
    // Viewer Fragen wie „Welche Anforderung verletzt dieses Bauteil?" ohne Rückfrage zu beantworten.
    if (report?.reportId) {
      queryParams['reportId'] = report.reportId;
      if (report.reportType) queryParams['reportType'] = report.reportType;
    }
    const coordinationIds = additionalFiles
      .map(file => file.id != null ? String(file.id).trim() : '')
      .filter(id => id.length > 0);
    if (coordinationIds.length > 0) {
      queryParams['coordination'] = coordinationIds.join(',');
    }
    this.router.navigate(['/projects', this.stateConfig.getProjectId(), 'files', projectFile.id], {queryParams});
  }

  /**
   * Prüft, ob aktuell ein Koordinationsmodell (mehrere Modelle) im Viewer geladen ist.
   */
  get isCoordinationView(): boolean {
    const {allIds} = this.getLoadedFileIds();
    return allIds.size > 1;
  }

  private handleElementsSelected(elements: SelectedElementRef[]): void {
    console.log('Elemente ausgewählt:', elements);

    // Die Selektion ist modellweit nur über die Kombination aus Modell-Id und
    // lokaler Element-Id eindeutig.
    const elementIds = elements.map(e => `${e.modelId}:${e.localId}`);
    this.lastSelection = elements;
    this.updateState({selectedElements: elementIds});
  }

  // ---------------------------------------------------------------------------
  // chatBIC: Kontext und Viewer-Aktionen
  // ---------------------------------------------------------------------------

  /**
   * Was chatBIC über den offenen Viewer wissen soll – oder `undefined`, solange die Engine nicht steht.
   *
   * <p>Klassen und Geschosse stehen drin, damit das Modell nur Ziele nennt, die
   * {@link applyChatAction} auch auflösen kann.
   */
  async snapshotForChat(fileName?: string): Promise<ChatViewerSnapshot | undefined> {
    if (!this.viewerReady) return undefined;

    const models = this.getModels();
    const index = await this.chatModelIndex();
    const shown = this.lastSelection.slice(0, CHAT_MAX_SELECTION);

    return {
      fileName,
      models: [...models.keys()],
      selection: await this.describeElements(shown),
      selectionCount: this.lastSelection.length,
      selectionProperties: this.lastSelection.length > 0
        ? BimViewerService.trimProperties(await this.bimApp.getSelectionProperties())
        : [],
      storeys: [...index.storeys.keys()],
      ifcClasses: [...index.classes.entries()]
        .map(([ifcClass, items]) => ({ifcClass, count: items.length}))
        .sort((a, b) => b.count - a.count),
      ifcTypes: [...index.types.entries()]
        .map(([name, entry]) => ({name, ifcClass: [...entry.ifcClasses].join('/'), count: entry.items.length}))
        .sort((a, b) => b.count - a.count),
      openingRelations: index.openingRelations,
      spaceBoundaries: index.spaceBoundaries,
    };
  }

  /**
   * Führt eine Anweisung von chatBIC im Viewer aus und meldet, was sie getroffen hat.
   *
   * <p>Das Ergebnis geht noch im selben Turn an das Modell zurück. Findet sich zum Ziel nichts,
   * sagt das zusätzlich ein Hinweis – der Nutzer soll nicht vor einem unveränderten Modell rätseln.
   *
   * <p>Wirft, wenn der Viewer nicht bereit ist oder ein Schnitt keine Geometrie findet; der Grund
   * geht dann als Fehler an das Modell.
   */
  async applyChatAction(action: ChatViewerAction): Promise<ChatViewerActionResult> {
    if (!this.viewerReady) throw new Error('Der Viewer ist noch nicht bereit – das Modell lädt noch.');

    switch (action.action) {
      case 'show_all':
        await this.bimApp.showAll();
        return {action: action.action};
      case 'reset_view':
        await this.bimApp.resetView();
        return {action: action.action};
      case 'reset_colors':
        await this.bimApp.resetColors();
        return {action: action.action};
      case 'clear_sections':
        this.bimApp.deleteClippingPlanes();
        return {action: action.action};
      case 'ghost': {
        // toggleGhost kennt nur Umschalten – deshalb erst den Zustand vergleichen.
        const wanted = action.enabled ?? !this.bimApp.isGhostEnabled();
        if (this.bimApp.isGhostEnabled() !== wanted) this.bimApp.toggleGhost();
        return {action: action.action};
      }
      case 'measure':
        if (action.mode === 'length' || action.mode === 'area') {
          this.bimApp.setToolMode(action.mode);
          return {action: action.action, note: 'Messwerkzeug ist aktiv – der Nutzer misst durch Klicken im Modell.'};
        }
        this.bimApp.setToolMode('select');
        return {action: action.action};
      case 'bcf_panel':
        if (action.enabled === false) this.bimApp.hideBCFPanel(); else this.bimApp.showBCFPanel();
        return {action: action.action};
      case 'open_topic': {
        const topicGuid = action.topicGuid ?? '';
        if (!this.bimApp.getTopic(topicGuid)) throw new Error('Dieses BCF-Thema gibt es im Viewer nicht.');
        const viewpoint = this.bimApp.getViewpointGuids(topicGuid)[0];
        if (viewpoint) await this.bimApp.applyViewpoint(viewpoint);
        const guids = this.bimApp.getTopicElementGuids(topicGuid);
        if (guids.length) await this.bimApp.selectElements(await this.refsByGuids(guids));
        this.bimApp.showBCFPanel();
        return {action: action.action, matched: guids.length};
      }
      case 'screenshot': {
        // Das Bild bleibt im Browser – es geht weder an den Agenten noch an den Server.
        const topicGuid = action.topicGuid;
        if (topicGuid && !this.bimApp.getTopic(topicGuid)) throw new Error('Dieses BCF-Thema gibt es im Viewer nicht.');
        const notes: string[] = [];
        if (action.download !== false) {
          const blob = await this.bimApp.captureImage();
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = `chatBIC-Ansicht-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;
          anchor.click();
          URL.revokeObjectURL(url);
          notes.push('Das Bild wurde heruntergeladen.');
        }
        if (topicGuid) {
          // Ein Ansichtspunkt trägt sein Vorschaubild selbst (Viewpoint.updateCamera nimmt es auf) –
          // damit ist es auch im BCF-Export.
          await this.bimApp.createViewpoint(topicGuid);
          notes.push('Die Ansicht hängt als Ansichtspunkt mit Bild am BCF-Thema.');
        }
        return {action: action.action, note: notes.join(' ')};
      }
      case 'export_bcf': {
        const topics = this.bimApp.getTopics();
        if (topics.length === 0) throw new Error('Im Viewer gibt es keine BCF-Themen zum Exportieren.');
        const bytes = await this.bimApp.exportBCF();
        const url = URL.createObjectURL(new Blob([bytes as BlobPart], {type: 'application/octet-stream'}));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'chatBIC-Themen.bcfzip';
        anchor.click();
        URL.revokeObjectURL(url);
        return {action: action.action, matched: topics.length, note: 'Die BCF-Datei wurde heruntergeladen.'};
      }
    }

    const warning = action.target?.relation
      ? BimViewerService.missingRelationWarning(action.target.relation, await this.chatModelIndex())
      : undefined;
    if (warning) {
      // Ohne Hinweis sähe der Nutzer nur die Ausgangselemente und hielte das für die ganze Antwort.
      this.notificationsService.showWarningMessage(warning);
      if (action.target!.relationOnly) return {action: action.action, matched: 0, warning};
    }

    const items = action.action === 'color_by' && isEmptyTarget(action.target) && !action.target?.relation
      ? (await this.chatModelIndex()).all
      : await this.resolveChatTarget(action.target);
    if (items.length === 0) {
      this.notificationsService.showInfoMessage('chatBIC hat im Modell keine passenden Elemente gefunden.');
      return {action: action.action, matched: 0, ...(warning ? {warning} : {})};
    }
    const result: ChatViewerActionResult = {action: action.action, matched: items.length, ...(warning ? {warning} : {})};

    // Die Engine nimmt OHNE Angabe alle Nachfahren der Raumstruktur mit (includeDescendants ist
    // dort per Default an): „isoliere die Räume" zeigte dann auch die Möblierung, die über
    // IfcRelContainedInSpatialStructure in den Räumen steckt. Deshalb dieselbe Regel wie der
    // Bauteiltypen-Baum: Nachfahren nur bei räumlichen Containern. Aggregate ohne eigene Geometrie
    // (IfcStair, IfcCurtainWall, …) ergänzt die Engine auch ohne Nachfahren um ihre Teile.
    const options = {includeDescendants: this.chatIncludeDescendants(action.target)};

    switch (action.action) {
      case 'select':
        await this.bimApp.selectElements(items, options);
        break;
      case 'isolate':
        await this.bimApp.isolateElements(items, options);
        break;
      case 'hide':
        await this.bimApp.setItemsVisible(items, false, options);
        return result;
      case 'focus':
        await this.bimApp.selectElements(items, options);
        await this.bimApp.focusSelection();
        break;
      case 'colorize':
        await this.bimApp.colorizeItems(items, action.color!);
        return result;
      case 'color_by':
        return {...result, ...(await this.colorByProperty(items, action.colorBy!))};
      case 'section':
        return {...result, planes: await this.applySection(items, action.section!)};
      case 'create_topic': {
        const guids = (await this.describeElements(items)).map(element => element.guid).filter((g): g is string => !!g);
        const topic = this.bimApp.createTopic({
          title: action.topic?.title || 'Thema aus chatBIC',
          description: action.topic?.description,
          priority: action.topic?.priority,
        });
        // Ohne instant fährt die Kamera sichtbar auf die Bauteile – hier sieht der Nutzer zu.
        if (guids.length) await this.bimApp.createViewpointFromElements(guids, topic.guid);
        this.bimApp.showBCFPanel();
        return {
          ...result, topicGuid: topic.guid,
          note: 'Das BCF-Thema existiert nur in dieser Viewer-Sitzung. Zum Aufheben: als BCF exportieren '
            + '(export_bcf) oder eine Aufgabe anlegen.',
        };
      }
    }
    if (action.color) {
      // colorizeSelection wirkt auf die Auswahl – beim Isolieren also erst auswählen.
      if (action.action === 'isolate') await this.bimApp.selectElements(items, options);
      await this.bimApp.colorizeSelection(action.color);
    }
    return result;
  }

  /**
   * Färbt jeden Wert eines Properties in einer eigenen Farbe ein.
   *
   * <p>Vorherige Einfärbungen fallen weg – sonst mischte sich die neue Legende mit einer alten.
   * Elemente ohne das Property bleiben ungefärbt und werden gezählt, damit der Agent das sagen
   * kann. Gibt es mehr Werte als Farben, teilen sich die seltensten die letzte („Sonstige").
   */
  private async colorByProperty(items: SelectedElementRef[], ref: ChatPropertyRef)
    : Promise<Pick<ChatViewerActionResult, 'legend' | 'withoutValue'>> {
    const rows = await this.chatPropertyRows(items);
    const groups = new Map<string, SelectedElementRef[]>();
    let withoutValue = 0;
    for (const item of items) {
      const row = rowsOf(rows.get(refKey(item)) ?? [], ref.property, ref.pset)[0];
      if (!row) {
        withoutValue++;
        continue;
      }
      const value = String(row.value);
      const group = groups.get(value);
      if (group) group.push(item); else groups.set(value, [item]);
    }

    const sorted = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
    const named = sorted.length > CHAT_COLOR_PALETTE.length ? sorted.slice(0, CHAT_COLOR_PALETTE.length - 1) : sorted;
    const rest = sorted.slice(named.length).flatMap(([, refs]) => refs);

    await this.bimApp.resetColors();
    const legend: NonNullable<ChatViewerActionResult['legend']> = [];
    for (const [i, [value, refs]] of named.entries()) {
      await this.bimApp.colorizeItems(refs, CHAT_COLOR_PALETTE[i]);
      legend.push({value, color: CHAT_COLOR_PALETTE[i], count: refs.length});
    }
    if (rest.length) {
      const color = CHAT_COLOR_PALETTE[CHAT_COLOR_PALETTE.length - 1];
      await this.bimApp.colorizeItems(rest, color);
      legend.push({value: `Sonstige (${sorted.length - named.length} Werte)`, color, count: rest.length});
    }
    return {legend, withoutValue};
  }

  /** Legt einen Schnitt um die Elemente; ersetzt vorhandene Schnitte. */
  private async applySection(items: SelectedElementRef[], section: ChatViewerSection): Promise<number> {
    const box = await this.bimApp.getItemsBoundingBox(items);
    if (!box) throw new Error('Die Zielelemente haben keine Geometrie – dafür lässt sich kein Schnitt legen.');
    const planes = sectionPlanes(box, section);
    // Zwei Grundrisse übereinander ergäben die Schnittmenge, also gar nichts Sinnvolles.
    this.bimApp.deleteClippingPlanes();
    for (const plane of planes) this.bimApp.createClippingPlane(plane.normal, plane.point);
    return planes.length;
  }

  /**
   * Beantwortet eine Abfrage von chatBIC über das offene Modell.
   *
   * <p>Ohne Ziel gilt das ganze Modell. Die Ergebnisse sind gekürzt (`truncated: true`), damit sie
   * das Kontextfenster des Modells nicht füllen.
   */
  async queryForChat(query: ChatViewerQuery): Promise<ChatViewerQueryResult> {
    if (!this.viewerReady) throw new Error('Der Viewer ist noch nicht bereit – das Modell lädt noch.');
    const index = await this.chatModelIndex();
    const items = isEmptyTarget(query.target) && !query.target?.relation
      ? index.all
      : await this.resolveChatTarget(query.target);

    switch (query.query) {
      case 'count': {
        const byClass: Record<string, number> = {};
        for (const item of items) {
          const ifcClass = index.classByItem.get(refKey(item)) ?? 'unbekannt';
          byClass[ifcClass] = (byClass[ifcClass] ?? 0) + 1;
        }
        return {query: query.query, count: items.length, byClass};
      }
      case 'properties': {
        const shown = items.slice(0, CHAT_QUERY_MAX_ELEMENTS);
        const rows = await this.chatPropertyRows(shown);
        const described = await this.describeElements(shown);
        const elements = shown.map((item, i) => {
          const properties: Record<string, Record<string, unknown>> = {};
          for (const row of (rows.get(refKey(item)) ?? []).slice(0, CHAT_QUERY_MAX_ROWS_PER_ELEMENT)) {
            (properties[row.pset] ??= {})[row.property] = row.value;
          }
          return {...described[i], properties};
        });
        return BimViewerService.fitResult(
          {query: query.query, count: items.length, truncated: items.length > shown.length, elements}, 'elements');
      }
      case 'property_names': {
        const rows = await this.chatPropertyRows(items);
        const counts = new Map<string, { pset: string; property: string; count: number }>();
        for (const item of items) {
          for (const row of rows.get(refKey(item)) ?? []) {
            const key = `${row.pset}\u0000${row.property}`;
            const entry = counts.get(key);
            if (entry) entry.count++; else counts.set(key, {pset: row.pset, property: row.property, count: 1});
          }
        }
        const properties = [...counts.values()].sort((a, b) => b.count - a.count);
        return BimViewerService.fitResult({
          query: query.query, count: items.length,
          truncated: properties.length > CHAT_QUERY_MAX_NAMES,
          properties: properties.slice(0, CHAT_QUERY_MAX_NAMES),
        }, 'properties');
      }
      case 'aggregate':
        return this.aggregateForChat(items, query, index);
      case 'bcf_topics': {
        const topics = this.bimApp.getTopics().slice(0, CHAT_QUERY_MAX_TOPICS).map(topic => ({
          guid: topic.guid,
          title: topic.title,
          status: topic.status,
          priority: topic.priority,
          elementCount: this.bimApp.getTopicElementGuids(topic.guid).length,
        }));
        return BimViewerService.fitResult({
          query: query.query, count: this.bimApp.getTopics().length,
          truncated: this.bimApp.getTopics().length > topics.length, topics,
        }, 'topics');
      }
      case 'property_values': {
        const ref = query.property!;
        const rows = await this.chatPropertyRows(items);
        const counts = new Map<string, number>();
        let withoutValue = 0;
        let min: number | undefined;
        let max: number | undefined;
        for (const item of items) {
          const row = rowsOf(rows.get(refKey(item)) ?? [], ref.property, ref.pset)[0];
          if (!row) {
            withoutValue++;
            continue;
          }
          const value = String(row.value);
          counts.set(value, (counts.get(value) ?? 0) + 1);
          const number = asNumber(row.value);
          if (number !== undefined) {
            min = min === undefined ? number : Math.min(min, number);
            max = max === undefined ? number : Math.max(max, number);
          }
        }
        const values = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({value, count}));
        return BimViewerService.fitResult({
          query: query.query, count: items.length,
          withValue: items.length - withoutValue, withoutValue,
          distinctValues: values.length,
          ...(min !== undefined ? {min, max} : {}),
          truncated: values.length > CHAT_QUERY_MAX_VALUES,
          values: values.slice(0, CHAT_QUERY_MAX_VALUES),
        }, 'values');
      }
    }
  }

  /**
   * Summen, Mittel und Extremwerte eines Zahlen-Properties, optional je Geschoss, Klasse, Typ oder
   * Property-Wert – „Gesamte Wandfläche je Geschoss". property_values reicht dafür nicht: Flächen sind
   * fast alle verschieden, die Werteliste wäre schon nach 50 Einträgen gekürzt.
   */
  private async aggregateForChat(items: SelectedElementRef[], query: ChatViewerQuery, index: ChatModelIndex)
    : Promise<ChatViewerQueryResult> {
    const ref = query.property!;
    const rows = await this.chatPropertyRows(items);
    const groupOf = await this.chatGroupKey(items, query, index, rows);

    const groups = new Map<string, { key: string; count: number; withValue: number; sum: number; min?: number; max?: number }>();
    let withoutValue = 0;
    for (const item of items) {
      const key = groupOf(item);
      const group = groups.get(key) ?? {key, count: 0, withValue: 0, sum: 0};
      groups.set(key, group);
      group.count++;
      const value = asNumber(rowsOf(rows.get(refKey(item)) ?? [], ref.property, ref.pset)[0]?.value);
      if (value === undefined) {
        withoutValue++;
        continue;
      }
      group.withValue++;
      group.sum += value;
      group.min = group.min === undefined ? value : Math.min(group.min, value);
      group.max = group.max === undefined ? value : Math.max(group.max, value);
    }
    const round = (n: number) => Math.round(n * 1000) / 1000;
    const list = [...groups.values()]
      .sort((a, b) => b.sum - a.sum)
      .map(g => ({
        key: g.key, count: g.count, withValue: g.withValue, sum: round(g.sum),
        avg: g.withValue ? round(g.sum / g.withValue) : undefined,
        ...(g.min !== undefined ? {min: round(g.min), max: round(g.max!)} : {}),
      }));
    const total = list.reduce((sum, g) => sum + g.sum, 0);
    return BimViewerService.fitResult({
      query: query.query, count: items.length, property: ref.property,
      total: round(total), withoutValue,
      truncated: list.length > CHAT_QUERY_MAX_VALUES,
      groups: list.slice(0, CHAT_QUERY_MAX_VALUES),
    }, 'groups');
  }

  /** Gruppenschlüssel je Element für {@link aggregateForChat}; ohne Gruppierung eine einzige Gruppe. */
  private async chatGroupKey(items: SelectedElementRef[], query: ChatViewerQuery, index: ChatModelIndex,
                             rows: Map<string, IfcPropertyRow[]>): Promise<(item: SelectedElementRef) => string> {
    const invert = (map: Map<string, SelectedElementRef[]>) => {
      const byItem = new Map<string, string>();
      for (const [key, refs] of map) for (const ref of refs) if (!byItem.has(refKey(ref))) byItem.set(refKey(ref), key);
      return byItem;
    };
    if (query.groupByProperty) {
      const ref = query.groupByProperty;
      return item => String(rowsOf(rows.get(refKey(item)) ?? [], ref.property, ref.pset)[0]?.value ?? 'ohne Wert');
    }
    switch (query.groupBy) {
      case 'storey': {
        const byItem = invert(index.storeys);
        return item => byItem.get(refKey(item)) ?? 'ohne Geschoss';
      }
      case 'type': {
        const byItem = invert(new Map([...index.types.entries()].map(([name, entry]) => [name, entry.items])));
        return item => byItem.get(refKey(item)) ?? 'ohne Typ';
      }
      case 'ifcClass':
        return item => index.classByItem.get(refKey(item)) ?? 'unbekannt';
      default:
        return () => 'gesamt';
    }
  }

  /** Kürzt die Liste eines Ergebnisses, bis es unter {@link CHAT_QUERY_MAX_CHARS} passt. */
  private static fitResult(result: ChatViewerQueryResult, listKey: string): ChatViewerQueryResult {
    let list = result[listKey] as unknown[];
    while (JSON.stringify(result).length > CHAT_QUERY_MAX_CHARS && list.length > 1) {
      list = list.slice(0, Math.ceil(list.length / 2));
      result = {...result, [listKey]: list, truncated: true};
    }
    return result;
  }

  /**
   * Properties der Elemente samt der Pseudo-Properties „Typ" und „Geschoss" (Satz
   * {@link ATTRIBUTE_PSET}) – je Modell-Set gecacht und erst bei Bedarf geladen, damit der
   * Kontext jeder Nachricht nicht darauf wartet.
   */
  private async chatPropertyRows(refs: SelectedElementRef[]): Promise<Map<string, IfcPropertyRow[]>> {
    const index = await this.chatModelIndex();
    if (this.chatPropertyCache?.key !== index.key) this.chatPropertyCache = {key: index.key, rows: new Map()};
    const cache = this.chatPropertyCache.rows;

    const models = this.getModels();
    const missing = refs.filter(ref => !cache.has(refKey(ref)));
    for (const [modelId, localIds] of BimViewerService.groupByModel(missing)) {
      const model = models.get(modelId);
      if (!model) continue;
      const loaded = await propertyRows(model as unknown as IfcRelationModel, localIds);
      for (const localId of localIds) {
        const key = `${modelId}:${localId}`;
        const rows = [...(loaded.get(localId) ?? [])];
        const type = index.typeByItem.get(key);
        if (type) rows.push({pset: ATTRIBUTE_PSET, property: 'Typ', value: type});
        const storey = index.storeyByItem.get(key);
        if (storey) rows.push({pset: ATTRIBUTE_PSET, property: 'Geschoss', value: storey});
        cache.set(key, rows);
      }
    }
    return new Map(refs.map(ref => [refKey(ref), cache.get(refKey(ref)) ?? []]));
  }

  /**
   * Ob die Aktion die Nachfahren der Raumstruktur einschließt.
   *
   * <p>Nur für eine reine Klassenangabe, und dort nach der Regel des Bauteiltypen-Baums
   * (Gebäude/Geschoss ja, Raum nein). Ein Geschossziel ist in {@link buildChatModelIndex} schon
   * vollständig aufgelöst; GUIDs und Auswahl gelten wie ein Klick genau für diese Elemente.
   */
  private chatIncludeDescendants(target: ChatViewerAction['target']): boolean {
    if (!target?.ifcClass || target.storey || target.ifcType || target.guids?.length || target.selection
      || target.relation || target.where?.length) return false;
    return this.bimApp.shouldCategorySelectionIncludeDescendants(target.ifcClass.toUpperCase());
  }

  /** Löst das Ziel einer Chat-Aktion in Elemente auf; Klasse und Geschoss ergeben die Schnittmenge. */
  private async resolveChatTarget(target: ChatViewerAction['target']): Promise<SelectedElementRef[]> {
    if (!target) return [];

    const sets: SelectedElementRef[][] = [];
    if (target.selection) sets.push(this.lastSelection);
    if (target.guids?.length) sets.push(await this.refsByGuids(target.guids));

    if (target.ifcClass || target.storey || target.ifcType) {
      const index = await this.chatModelIndex();
      if (target.ifcClass) sets.push(index.classes.get(target.ifcClass.toUpperCase()) ?? []);
      if (target.storey) sets.push(BimViewerService.findByName(index.storeys, target.storey) ?? []);
      if (target.ifcType) sets.push(BimViewerService.findByName(index.types, target.ifcType)?.items ?? []);
    }
    const where = target.where?.length ? target.where : undefined;
    if (sets.length === 0 && !where) return [];

    const key = refKey;
    // Nur ein Filter ohne weitere Angabe: dann wird das ganze Modell gefiltert.
    let base = sets.length === 0
      ? (await this.chatModelIndex()).all
      : sets.slice(1).reduce((result, next) => {
        const keys = new Set(next.map(key));
        return result.filter(ref => keys.has(key(ref)));
      }, sets[0]);
    if (where) {
      const rows = await this.chatPropertyRows(base);
      base = base.filter(ref => matchesAll(rows.get(key(ref)) ?? [], where));
    }
    if (!target.relation) return base;

    // Je Modell – die Relationen enden an der Modellgrenze. Die Klasse vergleicht per Präfix, damit
    // IFCWALL auch IFCWALLSTANDARDCASE trifft (IFC2X3-Exporte schreiben fast nur diese).
    const wantedClass = target.relationIfcClass?.toUpperCase();
    const related: SelectedElementRef[] = [];
    const models = this.getModels();
    for (const [modelId, localIds] of BimViewerService.groupByModel(base)) {
      const model = models.get(modelId);
      if (!model) continue;
      for (const item of await relatedVia(model as unknown as IfcRelationModel, localIds, target.relation)) {
        if (wantedClass && !String(item.category ?? '').toUpperCase().startsWith(wantedClass)) continue;
        related.push({modelId, localId: item.localId});
      }
    }
    const combined = target.relationOnly ? related : [...base, ...related];
    return [...new Map(combined.map(ref => [key(ref), ref])).values()];
  }

  /** Hinweis, wenn das Modell die verlangte Relation gar nicht trägt – sonst `undefined`. */
  private static missingRelationWarning(relation: NonNullable<ChatViewerAction['target']>['relation'],
                                        index: ChatModelIndex): string | undefined {
    if ((relation === 'fillings' || relation === 'hosts') && !index.openingRelations) {
      return 'Dieses Modell wurde ohne Öffnungsbeziehungen konvertiert. Türen und Fenster lassen sich ihren '
        + 'Wänden erst nach einer Neukonvertierung zuordnen.';
    }
    if ((relation === 'boundaries' || relation === 'spaces') && !index.spaceBoundaries) {
      return 'Dieses Modell enthält keine Raumbegrenzungen (IfcRelSpaceBoundary). Entweder wurde die Datei '
        + 'vor deren Unterstützung konvertiert, oder das IFC wurde ohne Raumbegrenzungen exportiert.';
    }
    return undefined;
  }

  private static groupByModel(refs: SelectedElementRef[]): Map<string, number[]> {
    const byModel = new Map<string, number[]>();
    for (const ref of refs) {
      const ids = byModel.get(ref.modelId);
      if (ids) ids.push(ref.localId); else byModel.set(ref.modelId, [ref.localId]);
    }
    return byModel;
  }

  private async refsByGuids(guids: string[]): Promise<SelectedElementRef[]> {
    const refs: SelectedElementRef[] = [];
    for (const [modelId, model] of this.getModels()) {
      const localIds = await model.getLocalIdsByGuids(guids);
      for (const localId of localIds) {
        if (localId != null) refs.push({modelId, localId});
      }
    }
    return refs;
  }

  /** GUID, Klasse und Name je Element – für die Auswahl im Chat-Kontext. */
  private async describeElements(refs: SelectedElementRef[]): Promise<ChatViewerSnapshot['selection']> {
    const models = this.getModels();
    const result: ChatViewerSnapshot['selection'] = [];
    for (const [modelId, localIds] of BimViewerService.groupByModel(refs)) {
      const model = models.get(modelId);
      if (!model) continue;
      const guids = await model.getGuidsByLocalIds(localIds);
      const data = await model.getItemsData(localIds, {attributes: ['Name'], attributesDefault: false});
      const typeNames = await typeNamesOf(model as unknown as IfcRelationModel, localIds);
      localIds.forEach((localId, i) => {
        const item = data.find(entry => (entry['_localId'] as any)?.value === localId) as any;
        result.push({
          guid: guids[i] ?? undefined,
          ifcClass: item?._category?.value,
          name: item?.Name?.value != null ? String(item.Name.value) : undefined,
          typeName: typeNames.get(localId),
        });
      });
    }
    return result;
  }

  private chatModelIndex(): Promise<ChatModelIndex> {
    const key = [...this.getModels().keys()].sort().join('|');
    if (!this.chatIndex || this.chatIndexKey !== key) {
      this.chatIndexKey = key;
      this.chatIndex = this.buildChatModelIndex(key).catch(err => {
        // Nicht cachen: ein Fehler beim Aufbau soll beim nächsten Versuch nicht wiederkommen.
        this.chatIndex = undefined;
        throw err;
      });
    }
    return this.chatIndex;
  }

  private async buildChatModelIndex(key: string): Promise<ChatModelIndex> {
    const classes = new Map<string, SelectedElementRef[]>();
    const seen = new Set<string>();
    const walkCategories = (nodes: CategoryTreeNode[] | undefined) => {
      for (const node of nodes ?? []) {
        // Öffnungen sind reine Datenobjekte ohne Geometrie – als Ziel zeigten sie nichts.
        if (node.ifcCategory && node.ifcCategory !== 'IFCOPENINGELEMENT' && node.items?.length) {
          for (const item of node.items) {
            const id = `${node.ifcCategory}|${item.modelId}:${item.localId}`;
            if (seen.has(id)) continue;
            seen.add(id);
            const list = classes.get(node.ifcCategory);
            if (list) list.push(item); else classes.set(node.ifcCategory, [item]);
          }
        }
        walkCategories(node.children);
      }
    };
    walkCategories(await this.bimApp.getCategoryTree());

    const storeys = new Map<string, SelectedElementRef[]>();
    const collect = (node: SpatialTreeNode, out: SelectedElementRef[]) => {
      if (node.localId != null) out.push({modelId: node.modelId, localId: node.localId});
      for (const id of node.childItemIds ?? []) out.push({modelId: node.modelId, localId: id});
      for (const child of node.children ?? []) collect(child, out);
    };
    // Die Raumstruktur der Engine folgt IfcRelAggregates UND IfcRelContainedInSpatialStructure:
    // unter einem Geschoss stehen die enthaltenen Bauteile, deren Teile (Treppenlauf unter Treppe)
    // und Räume samt ihrem Inhalt. Alles darunter gehört zum Geschoss.
    const walkSpatial = (nodes: SpatialTreeNode[] | undefined) => {
      for (const node of nodes ?? []) {
        // Ein Geschoss ohne Name trägt kein classLabel – sein `name` ist dann nur die Klasse.
        const isStorey = node.localId != null
          && (node.classLabel === 'IfcBuildingStorey' || node.name === 'IfcBuildingStorey');
        if (isStorey) {
          const name = node.classLabel
            ? node.name.slice(node.classLabel.length).trim() || node.name
            : `${node.name} ${node.localId}`;
          const items: SelectedElementRef[] = [...(storeys.get(name) ?? [])];
          for (const child of node.children ?? []) collect(child, items);
          // Typ-Ordner führen ihre Elemente als childItemIds UND als Kindknoten – einmal reicht.
          const unique = new Map(items.map(item => [`${item.modelId}:${item.localId}`, item]));
          storeys.set(name, [...unique.values()]);
        } else {
          walkSpatial(node.children);
        }
      }
    };
    walkSpatial(await this.bimApp.getSpatialTree());

    // Typen und Öffnungsrelationen kommen nicht aus den Bäumen der Engine, sondern direkt aus den
    // Fragments-Daten (siehe ifc-relations.ts).
    const types: ChatModelIndex['types'] = new Map();
    let openingRelations = true;
    let spaceBoundaries = true;
    for (const [modelId, model] of this.getModels()) {
      const relationModel = model as unknown as IfcRelationModel;
      for (const entry of await typeEntries(relationModel)) {
        const existing = types.get(entry.name) ?? {ifcClasses: new Set<string>(), items: []};
        existing.ifcClasses.add(entry.ifcClass);
        existing.items.push(...entry.instances.map(localId => ({modelId, localId})));
        types.set(entry.name, existing);
      }
      if (!(await hasOpeningRelations(relationModel))) openingRelations = false;
      if (!(await hasSpaceBoundaries(relationModel))) spaceBoundaries = false;
    }

    const classByItem = new Map<string, string>();
    const all: SelectedElementRef[] = [];
    for (const [ifcClass, items] of classes) {
      for (const item of items) {
        if (classByItem.has(refKey(item))) continue;
        classByItem.set(refKey(item), ifcClass);
        all.push(item);
      }
    }
    const typeByItem = new Map<string, string>();
    for (const [name, entry] of types) for (const item of entry.items) typeByItem.set(refKey(item), name);
    const storeyByItem = new Map<string, string>();
    for (const [name, items] of storeys) for (const item of items) storeyByItem.set(refKey(item), name);

    return {
      key, classes, storeys, types, openingRelations, spaceBoundaries,
      all, classByItem, typeByItem, storeyByItem,
    };
  }

  /** Eintrag per Name – exakt, sonst ohne Groß-/Kleinschreibung, sonst als Teilstring. */
  private static findByName<T>(entries: Map<string, T>, wanted: string): T | undefined {
    const exact = entries.get(wanted);
    if (exact) return exact;
    const needle = wanted.trim().toLowerCase();
    const names = [...entries.keys()];
    const match = names.find(n => n.toLowerCase() === needle) ?? names.find(n => n.toLowerCase().includes(needle));
    return match ? entries.get(match) : undefined;
  }

  private static trimProperties(groups: PropertyGroup[]): PropertyGroup[] {
    let budget = CHAT_MAX_PROPERTY_ROWS;
    const result: PropertyGroup[] = [];
    for (const group of groups) {
      if (budget <= 0) break;
      const rows = group.rows.slice(0, budget);
      budget -= rows.length;
      result.push({group: group.group, rows});
    }
    return result;
  }

  /**
   * Erstellt ein Koordinationsmodell aus den aktuell im Viewer geladenen Dateien.
   * Öffnet einen Dialog zur Namenseingabe und speichert das Modell über die API.
   */
  createCoordinationModel(): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Koordinationsmodelle zu erstellen.');
      return;
    }

    const {allIds} = this.getLoadedFileIds();

    if (allIds.size < 2) {
      this.notificationsService.showErrorMessage('Es müssen mindestens zwei Modelle geladen sein, um ein Koordinationsmodell zu erstellen.');
      return;
    }

    const projectId = this.stateConfig.getProjectId();

    // Dialog sofort öffnen und die Projektdateien asynchron nachladen -- analog zu
    // onFileSelectRequested. Vorher wurde getFiles() abgewartet, bevor der Dialog aufging;
    // bei grossen Projekten sah das mehrere Sekunden lang aus wie ein toter Button.
    const dialogRef = this.dialog.open(NewCoordinationModelComponent, {
      hasBackdrop: false,
      data: {
        ifcFiles: [],
        otherNames: [],
        loading: true,
      },
    });

    // Wird erst im getFiles-Callback gefüllt; bis dahin ist "Erstellen" im Dialog gesperrt.
    let verifiedIfcFileIds: string[] = [];

    dialogRef.afterClosed().subscribe((result: { name: string } | undefined) => {
      if (!result) return;
      // Doppelter Boden zum disabled-Zustand des Buttons.
      if (verifiedIfcFileIds.length < 2) return;

      this.fileControllerService.createCoordinationModel(
        verifiedIfcFileIds,
        result.name.trim(),
        projectId,
      ).subscribe({
        next: (created: ProjectFile) => {
          this.notificationsService.showSuccessMessage('Koordinationsmodell wurde erfolgreich erstellt.');
          // Auf die Route des gespeicherten Modells wechseln. Vorher endete der Flow mit dem Toast,
          // und die Antwort -- samt neuer Id -- wurde verworfen: das Modell war anschliessend nur
          // ueber den Dateimanager erreichbar, obwohl der Nutzer genau davor sass. Ein Sprung auf
          // die aufgeloeste IFC-Liste waere dagegen ein No-op, das ist die URL, die schon steht.
          if (created?.id) {
            this.router.navigateByUrl(coordinationModelPath(projectId, created.id));
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error('[CoordinationModel] Fehler beim Erstellen:', err);
          this.notificationsService.showErrorMessage(coordinationModelErrorMessage(err));
        },
      });
    });

    this.fileControllerService.getFiles(projectId).subscribe({
      next: (files) => {
        const allIfcFiles = files.filter(f => !f.isFolder && f.fileType === ProjectFile.FileTypeEnum.IFC);
        const loadedFiles = allIfcFiles.filter(f => f.id && allIds.has(f.id));

        // Verknuepft wird genau, was der Dialog auch anzeigt: die IDs aus der URL sind
        // ungeprueft (geteilte Links, geloeschte oder projektfremde Dateien, ein Modell aus
        // dem lokalen Datei-Dialog). Wuerden sie ungefiltert ans Backend gehen, lehnt
        // ProjectFileController#createCoordinationModel mit 400 (kein IFC) bzw. 404 (nicht im
        // Projekt) ab -- und der Nutzer saehe nur die generische Fehlermeldung, obwohl der
        // Dialog vorher plausibel aussah.
        verifiedIfcFileIds = loadedFiles.map(f => f.id).filter((id): id is string => !!id);
        if (verifiedIfcFileIds.length < 2) {
          verifiedIfcFileIds = [];
          dialogRef.componentInstance?.setLoadingFailed(
            'Es konnten nicht alle geladenen Modelle im Projekt gefunden werden. Bitte die Modelle neu laden.'
          );
          return;
        }

        // Nur die Namen auf Wurzelebene: das Modell entsteht ohne parentId, also dort. Vorher lief
        // die Dublettenpruefung projektweit und lehnte Namen ab, die nur in einem Unterordner
        // vergeben waren -- der Listen-Pfad in files.component prueft demgegenueber je Ordner.
        const childIds = new Set(files.flatMap(f => (f.children ?? []).map(c => c.id)));
        const otherNames = files
          .filter(f => !childIds.has(f.id))
          .map(f => f.name)
          .filter((n): n is string => !!n);
        dialogRef.componentInstance?.setLinkedFiles(loadedFiles, otherNames);
      },
      error: (err) => {
        console.error('[CoordinationModel] Fehler beim Laden der Projektdateien:', err);
        verifiedIfcFileIds = [];
        dialogRef.componentInstance?.setLoadingFailed('Dateien konnten nicht geladen werden.');
      }
    });
  }

  /**
   * Merkt sich die ProjectFile-Id eines Modells. Ohne sie kann ein Tag nicht gespeichert werden.
   */
  private registerModelFile(modelName: string, fileId?: string): void {
    if (!fileId) return;

    const existing = this.modelFileIds.get(modelName);
    if (existing && existing !== fileId) {
      console.warn(`[BimViewer] Zwei Dateien mit dem Namen "${modelName}" – der Tag der zweiten wird nicht gespeichert.`);
      return;
    }
    this.modelFileIds.set(modelName, fileId);
  }

  /**
   * Ermittelt die IDs der aktuell im Viewer geladenen Dateien aus der URL.
   */
  getLoadedFileIds(): { primaryFileId: string | null; coordinationIds: string[]; allIds: Set<string> } {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const segments = urlTree.root.children['primary']?.segments || [];
    const filesIndex = segments.findIndex(s => s.path === 'files');
    const primaryFileId = filesIndex >= 0 && segments.length > filesIndex + 1
      ? segments[filesIndex + 1].path : null;
    const coordinationParam = urlTree.queryParams['coordination'] || '';
    const coordinationIds = coordinationParam
      ? coordinationParam.split(',').filter((id: string) => id.trim().length > 0)
      : [];

    const allIds = new Set<string>();
    if (primaryFileId && primaryFileId !== '0') allIds.add(primaryFileId);
    coordinationIds.forEach((id: string) => allIds.add(id.trim()));

    // Kein Log hier: isCoordinationView ruft das aus einer Template-Bindung auf, also bei jeder
    // Change Detection – die Konsole lief damit bei jeder Mausbewegung im Viewer voll.
    return {primaryFileId, coordinationIds, allIds};
  }

  /** Schreibt einen geänderten Tag an die zugehörige Datei. */
  private persistModelTag(modelId: string): void {
    const fileId = this.modelFileIds.get(modelId);
    if (!fileId) return;

    const tag = this.bimApp.getModelTag(modelId) ?? '';
    if (this.lastPersistedTags.get(modelId) === tag) return;

    this.lastPersistedTags.set(modelId, tag);
    this.fileControllerService.updateModelTag(this.stateConfig.getProjectId(), fileId, tag).subscribe({
      error: (err: HttpErrorResponse) => {
        console.error('[BimViewer] Tag konnte nicht gespeichert werden:', err);
        // Zurücknehmen, damit ein erneuter Versuch mit demselben Wert nicht als "unverändert" gilt.
        this.lastPersistedTags.delete(modelId);
        this.notificationsService.showErrorMessage('Der Tag konnte nicht gespeichert werden.');
      },
    });
  }
}
